import { type ReactNode, useEffect, useRef } from 'react';
import css from './BarraFixa.module.css';

interface PropsBarraFixa {
  /** Nome da região para leitor de tela ("Salvar o pedido", "Desfazer"). */
  rotulo: string;
  tom?: 'normal' | 'erro' | 'contraste';
  /**
   * `salvar`: 68 px, encostada embaixo, na largura do conteúdo (Novo pedido).
   * `aviso`: 54 px, flutuando 16 px acima do pé (Desfazer, "salvo às…").
   */
  altura?: 'salvar' | 'aviso';
  /**
   * `polite` anuncia o que muda. Conteúdo que conta segundos ("Desfazer · 4 s") usa `off` e põe o
   * aviso num filho com o próprio aria-live, senão o leitor lê cada segundo.
   */
  vivo?: 'polite' | 'off';
  children: ReactNode;
}

/**
 * Barra de altura fixa por cima do pé da tela (decisão 37: nada empurra a lista). Fica fora do menu
 * no computador e acima da barra de abas no celular (variáveis do LayoutPainel). Fica no lugar dela
 * no DOM (não usa portal): o botão de enviar continua dentro do <form> e na ordem do Tab.
 */
export function BarraFixa({
  rotulo,
  tom = 'normal',
  altura = 'salvar',
  vivo = 'polite',
  children,
}: PropsBarraFixa) {
  const ref = useRef<HTMLDivElement>(null);

  // A barra de salvar publica a própria altura: o conteúdo ganha folga embaixo e o aviso (Desfazer)
  // flutua acima dela em vez de cobri-la (no celular ela tem duas linhas).
  useEffect(() => {
    const el = ref.current;
    if (altura !== 'salvar' || !el) return;
    const raiz = document.documentElement;
    const medir = () => raiz.style.setProperty('--folga-salvar', `${el.offsetHeight}px`);
    medir();
    const observador = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    observador?.observe(el);
    return () => {
      observador?.disconnect();
      raiz.style.removeProperty('--folga-salvar');
    };
  }, [altura]);

  return (
    <div ref={ref} className={`${css.posicao} ${css[altura]}`} data-barra={altura}>
      <section className={`${css.barra} ${css[`tom-${tom}`]}`} aria-label={rotulo} aria-live={vivo}>
        {children}
      </section>
    </div>
  );
}
